// SM25 high-level async command API
// Direct port of Sm25Client.java

import { Sm25Serial } from './sm25Serial';
import { Sm25Protocol, u16LE, le16 } from './sm25Protocol';
import {
	CMD_TEST_CONNECTION,
	CMD_ENROLL,
	CMD_ENROLL_STORE_RAM,
	CMD_GET_ENROLL_DATA,
	CMD_READ_TEMPLATE,
	CMD_WRITE_TEMPLATE,
	CMD_IDENTIFY,
	CMD_IDENTIFY_FREE,
	CMD_FP_CANCEL,
	CMD_GET_FW_VERSION,
	CMD_GET_ENROLL_COUNT,
	CMD_CLEAR_ALL,
	CMD_CLEAR_TEMPLATE,
	CMD_GET_EMPTY_ID,
	ERR_SUCCESS,
} from './types';

function ok(r) {
	return r !== null && r.ret === ERR_SUCCESS;
}

function codeFromData(data) {
	if (!data || data.length < 2) return 0;
	return (data[0] & 0xff) | ((data[1] & 0xff) << 8);
}

function resultFromResp(r, payloadOnSuccess) {
	const ret = ok(r) ? 0 : 1;
	const info = r ? codeFromData(r.data) : 0;
	return { ret, info, data: ret === 0 ? payloadOnSuccess : null };
}

export class Sm25Client {
	constructor() {
		this.serial = new Sm25Serial();
		this.protocol = new Sm25Protocol(this.serial);
	}

	get isConnected() {
		return this.serial.isConnected;
	}

	async connect() {
		await this.serial.connect();
	}

	async disconnect() {
		await this.serial.disconnect();
	}

	setGuideCallback(cb) {
		this.protocol.setGuideCallback(cb);
	}

	setDisconnectCallback(cb) {
		this.serial.setDisconnectCallback(cb);
	}

	async testConnection(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_TEST_CONNECTION, undefined, timeoutMs);
		return resultFromResp(r, undefined);
	}

	async enrollAndStoreInRam(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 30000;
		const r = await this.protocol.sendCommand(CMD_ENROLL_STORE_RAM, undefined, timeoutMs);
		return ok(r);
	}

	async getEnrollData(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const rd = await this.protocol.sendCommandExpectData(CMD_GET_ENROLL_DATA, undefined, timeoutMs);
		if (!rd.header || rd.header.ret !== ERR_SUCCESS || !rd.data) return null;
		return rd.data.data;
	}

	async enroll(templateId, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 30000;
		const r = await this.protocol.sendCommand(CMD_ENROLL, le16(templateId), timeoutMs);
		return resultFromResp(r, undefined);
	}

	async identify(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 30000;
		const r = await this.protocol.sendCommand(CMD_IDENTIFY, undefined, timeoutMs);
		const code = r ? codeFromData(r.data) : 0;
		const id = ok(r) ? code : null;
		return { ret: ok(r) ? 0 : 1, info: code, data: id };
	}

	async identifyFreeLoop(timeoutMs, onMatch, onError, shouldCancel) {
		const r = await this.protocol.sendCommand(CMD_IDENTIFY_FREE, undefined, timeoutMs);
		this._handleIdentifyFreeResponse(r, onMatch, onError);

		while (true) {
			if (shouldCancel()) {
				try { await this.fpCancel(); } catch (e) { /* ignore */ }
				break;
			}

			const next = await this.protocol.recvResponseSkippingGuides(timeoutMs);
			if (next.rcm !== CMD_IDENTIFY_FREE) {
				this._handleIdentifyFreeResponse(next, onMatch, onError);
				break;
			}

			this._handleIdentifyFreeResponse(next, onMatch, onError);
		}
	}

	_handleIdentifyFreeResponse(r, onMatch, onError) {
		if (!r) return;
		const v = u16LE(r.data, 0);
		if (r.ret === ERR_SUCCESS) {
			if (v >= 0xfff1 && v <= 0xfff4) return;
			onMatch(v);
		} else {
			onError(v);
		}
	}

	async readTemplate(templateId, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const rd = await this.protocol.sendCommandExpectData(CMD_READ_TEMPLATE, le16(templateId), timeoutMs);
		if (!rd.header || rd.header.ret !== ERR_SUCCESS || !rd.data || !rd.data.data) return null;
		const data = rd.data.data;
		if (data.length < 2 + 498) return null;
		return data.slice(2, 2 + 498);
	}

	async writeTemplate(templateId, template498, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const sizeParam = le16(template498.length);
		const ok1 = await this.protocol.sendCommand(CMD_WRITE_TEMPLATE, sizeParam, timeoutMs);
		if (!ok(ok1)) return resultFromResp(ok1, undefined);

		const payload = new Uint8Array(2 + template498.length);
		payload.set(le16(templateId), 0);
		payload.set(template498, 2);

		const r = await this.protocol.sendCommandThenUploadData(CMD_WRITE_TEMPLATE, sizeParam, payload, timeoutMs);
		return resultFromResp(r, undefined);
	}

	async fpCancel(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 3000;
		const r = await this.protocol.sendCommand(CMD_FP_CANCEL, undefined, timeoutMs);
		return resultFromResp(r, undefined);
	}

	async getFirmwareVersion(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_GET_FW_VERSION, undefined, timeoutMs);
		if (!ok(r) || !r.data) return null;
		return `${r.data[0] & 0xff}.${r.data[1] & 0xff}`;
	}

	async getEnrollCount(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_GET_ENROLL_COUNT, undefined, timeoutMs);
		if (!ok(r)) return -1;
		return u16LE(r.data, 0);
	}

	async getEmptyId(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_GET_EMPTY_ID, undefined, timeoutMs);
		if (!ok(r)) return -1;
		return u16LE(r.data, 0);
	}

	async clearTemplate(templateId, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_CLEAR_TEMPLATE, le16(templateId), timeoutMs);
		return ok(r);
	}

	async clearAllTemplates(timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 5000;
		const r = await this.protocol.sendCommand(CMD_CLEAR_ALL, undefined, timeoutMs);
		return ok(r);
	}
}
